<template>
  <div class="tour my-personalized-tour" :class="{'map-open': isMapOpen}" role="presentation">
    <header-mobile>
      <h3>{{ $t('message.personalizedTour') }}</h3>
    </header-mobile>
    <main class="content" aria-label="Main content">
      <GeoLocationNotification />
      <div
        v-if="description"
        v-html="$tr(description)"
        class="description"
        role="presentation"
      />
      <div
        v-else-if="personalizedTour.myTourText1 || personalizedTour.myTourText2"
        class="description"
        role="presentation"
      >
        <p>
          {{ $t("personalizedTour.myTourText1") }}<br/>
          {{ $t("personalizedTour.myTourText2") }}
        </p>
      </div>
      <poi-map :poiList="poiList"
               :position="position"
               :directions="true"
               :visible="isMapOpen"
               ref="map" />
      <div class="start-container" role="presentation">
        <button class="btn btn-primary main-background secondary-text"
             :disabled="poiList.length === 0"
             @click="startTour"
             @keypress="startTour">
             <img src="@/assets/start-tour.png" alt="">{{ $t("personalizedTour.startTour") }}
        </button>
        <button class="btn btn-primary main-background secondary-text save-tour"
             :disabled="poiList.length === 0"
             @click="showTourSharePopup"
             @keypress="showTourSharePopup">{{ $t("personalizedTour.saveTour") }}
        </button>
        <div v-if="isShowTourSharePopup" class="share-tour" role="dialog" aria-label="Save tour popup">
          <div class="share-notes" role="presentation">
            <button @click="hideTourSharePopup" aria-label="Close popup" class='btn-close'> &times;</button>
            <div class="overlay" v-if="tourSent" aria-label="Tour is saved">
              <div class="success-popup" role="presentation">
                <h3>{{ $t('personalizedTour.success') }}</h3>
                <p>{{ $t('personalizedTour.tourEmailed') }}.</p>
                <button class="btn btn-primary main-background" aria-label="Close success popup" @click="closeSuccessPopup()">{{ $t('button.ok') }}</button>
              </div>
            </div>
            <form>
              <label for="share_note_first_name">{{ $t('personalizedTour.firstName') }}</label>
              <input id="share_note_first_name" type="text" placeholder="Joe" v-model="formData.firstName" name="firstName">
              <label for="share_note_last_name">{{ $t('personalizedTour.lastName') }}</label>
              <input id="share_note_last_name" type="text" placeholder="Smith" v-model="formData.lastName" name="lastname">
              <label for="share_note_email">{{ $t('socialMedia.email') }}</label>
              <input id="share_note_email" type="email" placeholder="joe@smith.com" v-model="formData.email" name="email">
              <div class="opt-in" v-if="isNCEnabled" role="presentation">
                <input id="optin" type="checkbox" v-model="formData.ncOptIn">
                <label for="optin">{{ $tr(shareForm.optin_message) }}</label>
              </div>
            </form>
            <button
              :disabled="!formData.email"
              type="submit"
              @click="shareTour"
              class="btn btn-primary main-background secondary-text"
              aria-label="Send form"
            >
              {{ $t('button.emailMyTour') }}
            </button>
          </div>
        </div>
      </div>
      <div class="btn-container" role="presentation">
        <button
          class="btn btn-secondary main-color"
          :disabled="!mapAllowed"
          @click="isMapOpen = !isMapOpen"
        >
          <img src="@/assets/map.png" alt="">
          {{ $t("personalizedTour.viewRoute") }}
        </button>
        <router-link
          :to="{name: 'PersonalizedEdit', params: {category: categories[0].id }}"
          tag="a"
          class="btn btn-secondary main-color"
          v-if="categories.length > 0"
        >
          <img src="@/assets/point.png" alt="">
          {{ $t("personalizedTour.editList") }}
        </router-link>
      </div>
      <poi-list :poiList="poiList" />
    </main>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex'
import HeaderMobile from '@/components/HeaderMobile'
import PoiList from '@/components/PoiList'
import PoiMap from '@/components/PoiMap'
import GeoLocationNotification from '@/components/GeoLocationNotification'

export default {
  name: 'PersonalizedTour',
  components: {
    'header-mobile': HeaderMobile,
    PoiList,
    PoiMap,
    GeoLocationNotification
  },
  data () {
    return {
      formData: {
        firstName: '',
        lastName: '',
        email: '',
        ncOptIn: false
      },
      isShowTourSharePopup: false,
      tourSent: false,
      isMapOpen: false
    }
  },
  created () {
    this.startLoad()
    if (this.$route.query.tour && this.student) {
      this.getPersonalizedTourById({ student: this.student, tourId: this.$route.query.tour })
    }
    if (this.categories.length === 0) {
      this.getCategories()
    }
  },
  mounted () {
    this.endLoad()
  },
  methods: {
    ...mapMutations(['startLoad', 'endLoad']),
    ...mapMutations('poi', ['setCurrentPoiList', 'setCurrentTour', 'setUnfinishedTour']),
    ...mapMutations('personalized', ['setSelectedPOIs']),
    ...mapActions('personalized', ['getCategories', 'getPersonalizedTourById', 'savePersonalizedTour']),
    startTour: function () {
      this.startLoad()
      this.getDirections().then((route) => {
        this.endLoad()
        this.setCurrentPoiList(route)
        this.setCurrentTour({ type: 'Personalized' })
        this.setUnfinishedTour({ type: 'Personalized', poiList: route })
        this.$router.push({ name: 'PoiDirections', params: { id: route[0].id } })
      }).catch(() => {
        this.endLoad()
      })
    },
    shareTour: function () {
      const poiIds = this.poiList.map(element => element.id)
      this.savePersonalizedTour({ student: this.student, poiIds: poiIds }).then((response) => {
        this.axios.post(`/email/tour/${this.student}`, this.formData).then(response => {
          if (response.data.success) {
            this.tourSent = true
            this.$nextTick(() => this.$focusInsidePopup())
          }
        })
      })
    },
    closeSuccessPopup: function (event) {
      this.isShowTourSharePopup = false
      this.tourSent = false
      this.formData = {
        firstName: '',
        lastName: '',
        email: '',
        ncOptIn: false
      }
      this.$nextTick(() => {
        if (event.pointerType === "mouse" || (event.type === 'keypress' && event.keyCode === 13) ) {
          this.$focusOutsidePopup()
        }
      })
    },
    getDirections: function () {
      // Directly accessing map component to allow Promise callback
      return this.$refs.map.getDirections()
    },
    hideTourSharePopup: function (event) {
      this.isShowTourSharePopup = false
      this.$nextTick(() => {
        if (event.pointerType === "mouse" || (event.type === 'keypress' && event.keyCode === 13) ) {
          this.$focusOutsidePopup()
        }
      })
    },
    showTourSharePopup: function () {
      this.isShowTourSharePopup = true
      this.$nextTick(() => this.$focusInsidePopup())
    }
  },
  computed: {
    ...mapState({
      description: state => state.college.settings.personalized_tour.tour_page.description,
      shareForm: state => state.college.settings.share_form,
      isNCEnabled: state => state.college.namecaptureToken != null
    }),
    ...mapState('personalized', {
      categories: state => state.categoriesList,
      poiList: state => state.poiSelected
    }),
    ...mapState('student', {
      student: state => state.student
    }),
    ...mapGetters({
      position: 'getUserPosition'
    }),
    mapAllowed: function () {
      return this.poiList !== undefined && this.poiList.length > 0 && this.position !== undefined
    }
  },
  watch: {
    student: function (newVal, oldVal) {
      if (newVal && this.$route.query.tour) {
        this.getPersonalizedTourById({ student: this.student, tourId: this.$route.query.tour })
      }
    }
  }
}
</script>
